import { useEffect, useMemo, useState, useCallback } from 'react';

import { useHistory, useParams } from 'react-router-dom';
import { AxiosResponse } from 'axios';

import {
  Company,
  INTERFACE_PERMISSIONS,
  MANAGEMENT_PERMISSIONS,
  Passport,
  TEAMS_PERMISSIONS,
  TIME_INTERFACE_PERMISSIONS,
  UserPreviewData,
} from '../../../types';
import { getUrlItems } from '../../../utils/url';
import { AnyOnboarding } from '../../../utils/onboardingStatus';
import { GetUserData } from '../../../api/types';
import { useBooleanState } from '../../../hooks';
import { usePermissions } from '../../../hooks/usePermissions';

interface Props {
  getEmployeePreviewRequest: (params: GetUserData) => Promise<AxiosResponse>;
  getPassportRequest: (params: GetUserData) => Promise<AxiosResponse>;
  getRtwRequest: (params: GetUserData) => Promise<AxiosResponse>;
  // Optional: when provided, the worker's onboarding status is fetched alongside
  // passport/RTW. The source and the shape differ per app — pay-app reads a per-module
  // status off the company worker, Legend a roll-up split per company (by_company) off
  // /admin/v1/users/{id}/basic — so only the `onboarding` field matters here.
  getOnboardingRequest?: (
    params: GetUserData
  ) => Promise<AxiosResponse<{ onboarding?: AnyOnboarding | null }>>;
  selectedCompany?: Company;
}

export const useEmployeeProfileData = ({
  getEmployeePreviewRequest,
  getRtwRequest,
  getPassportRequest,
  getOnboardingRequest,
  selectedCompany,
}: Props) => {
  const [userPreviewData, setUserPreviewData] = useState<UserPreviewData>();
  const [loading, setLoadingTrue, setLoadingFalse] = useBooleanState(false);
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [passport, setPassport] = useState<Passport>();
  const [rtw, setRtw] = useState<any>();
  const [workerOnboarding, setWorkerOnboarding] = useState<AnyOnboarding | null>();
  // Rights come from the permission context, not a prop: it is the same selected
  // position pay-app used to pass down, and it also knows about owners, Legend
  // staff and per-resource grants. canAnywhere throughout — every right below can
  // be granted on a single location, team or pay schedule.
  const { canAny, canAnywhere, ready } = usePermissions();

  // The T&A Settings tab adds, updates and removes the worker's site shifts.
  const showTimeSection = canAnywhere(TIME_INTERFACE_PERMISSIONS.UPDATE_WORKERS);
  // Passport and Right to Work — exactly what can_view_worker_documents covers.
  // can_view_worker_profiles also opens them: that is the right Payroll roles hold.
  const canViewWorkerDocuments =
    canAnywhere(TEAMS_PERMISSIONS.CAN_VIEW_WORKER_DOCUMENTS) ||
    canAnywhere(MANAGEMENT_PERMISSIONS.VIEW_WORKER_PROFILES);

  const selectedCompanyId = selectedCompany?.uuid ?? '';
  const urlItems = getUrlItems(id);
  const userId = urlItems?.id;

  const hasPayrollPermissions = useMemo(
    () => canAny([INTERFACE_PERMISSIONS.PAYROLL, INTERFACE_PERMISSIONS.PAYROLL_OUTSOURCED]),
    [canAny]
  );

  const hasPermissionForCreateQualification = useMemo(
    () => canAnywhere(TEAMS_PERMISSIONS.CAN_CREATE_QUALIFICATION),
    [canAnywhere]
  );

  const getUserPreviewData = useCallback(async () => {
    const urlItems = getUrlItems(id);
    const userId = urlItems?.id;
    if (!userId) {
      return;
    }
    try {
      setLoadingTrue();
      const res = await getEmployeePreviewRequest({ companyId: selectedCompanyId, userId });
      getPassport(userId);
      getRTW(userId);
      getWorkerOnboarding(userId);
      setUserPreviewData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingFalse();
    }
  }, [id, selectedCompanyId]);

  const getPassport = async (userId: string) => {
    if (!canViewWorkerDocuments) {
      return;
    }
    try {
      const res = await getPassportRequest({ companyId: selectedCompanyId, userId });
      setPassport(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const getRTW = async (userId: string) => {
    if (!canViewWorkerDocuments) {
      return;
    }
    try {
      const { data } = await getRtwRequest({ companyId: selectedCompanyId, userId });
      setRtw(data.items?.length ? data.items[0] : data);
    } catch (e) {
      console.error(e);
    }
  };

  const getWorkerOnboarding = async (userId: string) => {
    // No company guard: Legend never selects one and reads onboarding off the user
    // endpoint. Swallow errors — before the gateway ships this the request 404s,
    // and the profile must render exactly as it does today
    if (!getOnboardingRequest) {
      return;
    }
    try {
      const res = await getOnboardingRequest({ companyId: selectedCompanyId, userId });
      setWorkerOnboarding(res.data?.onboarding ?? null);
    } catch (e) {
      console.error(e);
    }
  };

  const refetchWorkerOnboarding = useCallback(async () => {
    if (userId) {
      await getWorkerOnboarding(userId);
    }
  }, [userId, selectedCompanyId, getOnboardingRequest]);

  useEffect(() => {
    // Waits for `ready`: before the position resolves every predicate answers
    // false, and the document requests inside would be skipped for good.
    if (!ready) {
      return;
    }
    getUserPreviewData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  const goToEditView = () => {
    history.push(`${history.location.pathname}/personal`);
  };

  const contextOptions = useMemo(
    () => ({
      fetchUserPreviewData: getUserPreviewData,
    }),
    [getUserPreviewData]
  );

  return {
    userPreviewData,
    userPreviewDataLoading: loading,
    passport,
    rtw,
    workerOnboarding,
    refetchWorkerOnboarding,
    hasPayrollPermissions,
    hasPermissionForCreateQualification,
    selectedCompanyId,
    userId,
    contextOptions,
    showTimeSection,
    // No hasPermissionsToEditAgencyData: saving that block calls admin/v1, which
    // is Legend-only, so no permission in a customer app can make it work.
    // Legend passes the flag to UserPreview itself.
    isShowAgencyData: hasPayrollPermissions,
    goToEditView,
    getUserPreviewData,
  };
};
